import React, { useState } from "react";
import "./registerContributor.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const RegisterContributor = () => {
  const [personalWebsite, setPersonalWebsite] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [experiences, setExperiences] = useState([
    { id: 1, title: "", genre: "", publisher: "", date: "", url: "" },
  ]);
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [socialInputs, setSocialInputs] = useState({
    twitter: "",
    instagram: "",
    linkedin: "",
    facebook: "",
  });

  // Validation states
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  // Validation functions
  const validateRequiredField = (value, fieldName) => {
    if (!value || value.trim() === "") {
      return `${fieldName} is required`;
    }
    return "";
  };

  const validateAtLeastOneExperience = (experiences) => {
    if (!experiences || experiences.length === 0) {
      return "At least one work experience is required";
    }

    // Check if at least one experience has meaningful content
    const hasValidExperience = experiences.some(exp =>
      (exp.title && exp.title.trim() !== "") ||
      (exp.genre && exp.genre.trim() !== "") ||
      (exp.publisher && exp.publisher.trim() !== "") ||
      (exp.date && exp.date !== "") ||
      (exp.url && exp.url.trim() !== "")
    );

    return hasValidExperience ? "" : "At least one work experience entry must be properly filled with meaningful content";
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = {};

    // Check terms agreement
    if (!agreedToTerms) {
      newErrors.terms = "Please agree to the terms and conditions";
    }

    // Check at least one experience
    const experienceError = validateAtLeastOneExperience(experiences);
    if (experienceError) {
      newErrors.experiences = experienceError;
    }

    // Check description
    const descriptionError = validateRequiredField(description, "Description");
    if (descriptionError) {
      newErrors.description = descriptionError;
    }

    setErrors(newErrors);

    // If there are validation errors, show toast and return
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors in the form before submitting.");
      return;
    }

    setIsSubmitting(true);

    try {
      console.log(
        JSON.stringify({
          experiences,
          description,
          personalWebsite,
          twitter: socialInputs.twitter,
          instagram: socialInputs.instagram,
          linkedin: socialInputs.linkedin,
          facebook: socialInputs.facebook,
        })
      );
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/contributor/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            experiences,
            description,
            personalWebsite,
            twitter: socialInputs.twitter,
            instagram: socialInputs.instagram,
            linkedin: socialInputs.linkedin,
            facebook: socialInputs.facebook,
          }),
        }
      );
      if (response.status === 401) {
        toast.error("You must be logged in to register as a contributor");
        navigate("/");
        return;
      }
      if (response.ok) {
        const data = await response.json();
        console.log("Contributor registered successfully:", data);
        toast.success("Registration application forwarded to admin! Wait for approval.");
        navigate("/search");
      } else {
        const errorData = await response.json();
        toast.error(`Registration failed: ${errorData.message}`);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while registering. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const addExperience = () => {
    setExperiences([
      ...experiences,
      {
        id: experiences.length + 1,
        title: "",
        genre: "",
        publisher: "",
        date: "",
        url: "",
      },
    ]);
  };

  const updateExperience = (id, field, value) => {
    setExperiences(
      experiences.map((exp) =>
        exp.id === id ? { ...exp, [field]: value } : exp
      )
    );
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(URL.createObjectURL(file));
    }
  };

  const handleBackClick = () => {
    navigate("/search");
  };

  const handleSocialInputChange = (field, value) => {
    setSocialInputs({ ...socialInputs, [field]: value });
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <img 
            src="/logo.png" 
            alt="Sports Hub Logo" 
            className="h-[50px] sm:h-[60px] mx-auto mb-4 sm:mb-6 cursor-pointer hover:opacity-80 transition-opacity drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" 
            onClick={() => navigate(-1)}
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-[#3498db] mb-2">
            Register as a Contributor
          </h1>
          <p className="text-[#57606f] mt-2 text-sm sm:text-base">
            Create account by filling the form below
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.05)] rounded-2xl p-4 sm:p-8 space-y-6 sm:space-y-8 backdrop-blur-[10px] border border-white/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Writer Experience <span className="text-red-500">*</span></h2>
              {errors.experiences && (
                <div className="text-[#dc3545] text-[13px] mb-2.5 flex items-center gap-1 bg-[#fff5f5] p-3 rounded-lg">
                  <span className="text-xs">⚠</span>
                  {errors.experiences}
                </div>
              )}
                {experiences.map((exp, index) => (
                  <div key={exp.id} className="border border-[#ddd] rounded-lg p-4 mb-4 space-y-4">
                    <h3 className="text-base font-medium text-gray-700">Work Experience {index + 1} <span className="text-gray-500 text-sm">(Fill at least one)</span></h3>
                    <input
                      type="text"
                      placeholder="Enter Title"
                      value={exp.title}
                      onChange={(e) =>
                        updateExperience(exp.id, "title", e.target.value)
                      }
                      className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    />
                    <input
                      type="text"
                      placeholder="Enter Genre or Topic"
                      value={exp.genre}
                      onChange={(e) =>
                        updateExperience(exp.id, "genre", e.target.value)
                      }
                      className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    />
                    <input
                      type="text"
                      placeholder="Enter Publisher/Publication"
                      value={exp.publisher}
                      onChange={(e) =>
                        updateExperience(exp.id, "publisher", e.target.value)
                      }
                      className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    />
                    <input
                      type="date"
                      placeholder="Enter date"
                      value={exp.date}
                      onChange={(e) =>
                        updateExperience(exp.id, "date", e.target.value)
                      }
                      className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    />
                    <input
                      type="url"
                      placeholder="Enter Publication URL"
                      value={exp.url}
                      onChange={(e) =>
                        updateExperience(exp.id, "url", e.target.value)
                      }
                      className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    />
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addExperience}
                  className="w-full h-11 px-4 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100 mb-4"
                >
                  + Add more experience
                </button>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description of the Writer <span className="text-red-500">*</span></label>
                <textarea
                  placeholder="Enter description of your writing experience and expertise"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.description 
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]" 
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  rows="4"
                ></textarea>
                {errors.description && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.description}
                  </div>
                )}
                <div className="toggle-field">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={personalWebsite !== ""}
                      onChange={() =>
                        setPersonalWebsite(personalWebsite ? "" : "http://")
                      }
                    />
                    <span className="slider round"></span>
                  </label>
                  <span>Personal website <span className="optional-text">(Optional)</span></span>
                </div>

                {personalWebsite !== "" && (
                  <input
                    type="url"
                    placeholder="Enter personal website URL"
                    value={personalWebsite}
                    onChange={(e) => setPersonalWebsite(e.target.value)}
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                )}

                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mt-6 mb-4">Digital Presence <span className="text-gray-500 text-sm font-normal">(Optional)</span></h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Twitter"
                    value={socialInputs.twitter}
                    onChange={(e) =>
                      handleSocialInputChange("twitter", e.target.value)
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <input
                    type="text"
                    placeholder="Instagram"
                    value={socialInputs.instagram}
                    onChange={(e) =>
                      handleSocialInputChange("instagram", e.target.value)
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <input
                    type="text"
                    placeholder="LinkedIn"
                    value={socialInputs.linkedin}
                    onChange={(e) =>
                      handleSocialInputChange("linkedin", e.target.value)
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <input
                    type="text"
                    placeholder="Facebook"
                    value={socialInputs.facebook}
                    onChange={(e) =>
                      handleSocialInputChange("facebook", e.target.value)
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                </div>

                <div className="mt-6 p-4 border border-[#ddd] rounded-lg">
                  <label htmlFor="terms" className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={agreedToTerms}
                      onChange={() => setAgreedToTerms(!agreedToTerms)}
                      className={`mr-3 w-5 h-5 ${errors.terms ? "border-[#dc3545]" : ""}`}
                    />
                    <span className="text-sm text-gray-700">Agree to league terms and conditions <span className="text-red-500">*</span></span>
                  </label>
                  {errors.terms && (
                    <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                      <span className="text-xs">⚠</span>
                      {errors.terms}
                    </div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6">
                  <button 
                    type="submit" 
                    className="flex-1 h-11 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold uppercase tracking-[0.5px] shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 text-sm sm:text-base" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "REGISTERING..." : "SAVE"}
                  </button>
                  <button
                    type="button"
                    className="flex-1 h-11 bg-white text-[#3498db] border border-[#3498db] rounded-lg hover:bg-[#f0f8ff] transition-all duration-300 font-semibold hover:scale-105 active:scale-100 text-sm sm:text-base"
                    onClick={handleBackClick}
                  >
                    BACK
                  </button>
                </div>
              </div>

              <div className="lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo <span className="text-gray-500">(Optional)</span></label>
                <input
                  type="file"
                  id="photo-upload"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  style={{ display: "none" }}
                />
                <label htmlFor="photo-upload" className="flex flex-col items-center justify-center w-full h-48 border-2 border-[#ddd] border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors overflow-hidden">
                  {photo ? (
                    <img
                      src={photo}
                      alt="Uploaded"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      <i className="fas fa-camera text-gray-500 text-3xl mb-2"></i>
                      <span className="text-sm text-gray-500">Upload photo</span>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </form>
        </div>
      </div>
  );
};

export default RegisterContributor;
